import React, { useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper'
import { Navigation, Autoplay } from 'swiper/modules'
import Image from 'next/image'
import Link from 'next/link'
import LabelCard from '../Cards/LabelCard'
import { NewsArticle } from '@/models/news.model'
import 'swiper/css'
import 'swiper/css/navigation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons'
import { formatDistanceToNow } from 'date-fns'
import { vi } from 'date-fns/locale'

interface Props {
  data: NewsArticle[]
  isLoading: boolean
}

const LargeCard = ({ data, isLoading }: Props) => {
  const swiperRef = useRef<SwiperType | null>(null)

  const renderSkeleton = () => (
    <div className="animate-pulse">
      <div className="relative w-full h-[350px] bg-gray-300" />
      <div className="p-4">
        <div className="h-6 bg-gray-300 rounded-md mb-2 w-3/4" />
        <div className="h-4 bg-gray-300 rounded-md w-full" />
        <div className="h-4 bg-gray-300 rounded-md w-5/6 mt-1" />
      </div>
    </div>
  )

  return (
    <div className="relative group">
      <Swiper
        modules={[Navigation, Autoplay]}
        spaceBetween={30}
        slidesPerView={1}
        loop
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
      >
        {isLoading
          ? Array.from({ length: 3 }).map((_, index) => (
              <SwiperSlide key={index}>{renderSkeleton()}</SwiperSlide>
            ))
          : data.map((slide, index) => (
              <SwiperSlide key={index} className="overflow-hidden">
                <Link href={`/${slide.slug}`} className="block">
                  <article className="bg-white shadow-md">
                    {/* Image Section */}
                    <div className="relative w-full h-[350px] group">
                      <Image
                        src={`${process.env.NEXT_PUBLIC_IMAGE_URL}${slide.imageUrl}`}
                        alt={slide.title || 'title'}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 680px"
                        className="object-cover"
                        priority
                      />
                      <LabelCard categoryId={slide.categoryId} categoryName={slide.categoryName} />

                      {/* Overlay and Navigation */}
                      <div className="absolute inset-0 flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                        {/* Left Overlay */}
                        <div className="absolute top-0 left-0 h-full w-[80px] bg-gradient-to-r from-black/50 to-transparent pointer-events-none"></div>

                        {/* Right Overlay */}
                        <div className="absolute top-0 right-0 h-full w-[80px] bg-gradient-to-l from-black/50 to-transparent pointer-events-none"></div>

                        {/* Left Button */}
                        <button
                          aria-label="leftbutton"
                          className="custom-prev bg-white/80 hover:bg-white text-black rounded-full p-5 shadow-lg z-30"
                          onClick={(e) => {
                            e.preventDefault() // Prevent link click
                            swiperRef.current?.slidePrev()
                          }}
                        >
                          <FontAwesomeIcon icon={faAngleLeft} className="text-2xl" />
                        </button>

                        {/* Right Button */}
                        <button
                          aria-label="rightbutton"
                          className="custom-next bg-white/80 hover:bg-white text-black rounded-full p-5 shadow-lg z-30"
                          onClick={(e) => {
                            e.preventDefault() // Prevent link click
                            swiperRef.current?.slideNext()
                          }}
                        >
                          <FontAwesomeIcon icon={faAngleRight} className="text-2xl" />
                        </button>
                      </div>
                    </div>

                    {/* Title & Description */}
                    <div className="p-4">
                      <div className="text-sm text-gray-500 mb-2">
                        <span>
                          {formatDistanceToNow(new Date(slide.publishDate), {
                            addSuffix: true,
                            locale: vi,
                          })}
                        </span>
                      </div>
                      {/* Title */}
                      <h3 className="text-[18px] font-title font-semibold mb-2 group-hover:text-primary">
                        {slide.title}
                      </h3>
                      {/* Description */}
                      <p className="text-textGray text-justify mb-2 line-clamp-5">
                        {slide.description}
                      </p>
                      {/* Views */}
                      <div className="text-sm text-gray-500">
                        Lượt xem: <span className="font-semibold">{slide.view}</span>
                      </div>
                    </div>
                  </article>
                </Link>
              </SwiperSlide>
            ))}
      </Swiper>
    </div>
  )
}

export default LargeCard
